import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  MdCalendarMonth,
  MdCheck,
  MdWbSunny,
  MdWarning,
  MdEventBusy,
} from 'react-icons/md';
import employeeAttendanceAPI from '../api/employeeAttendanceAPI';
import { useEmployeeAttendanceAuth } from '../context/EmployeeAttendanceAuthContext';
import { defaultDateRange } from '../utils/attendanceFormatters';
import {
  computeSummaryStats,
  filterRecordsByMonth,
  filterRecordsByStatus,
  getMonthLabel,
  getRecordsForStatKey,
  recordsByDateMap,
} from '../utils/historyAnalytics';
import HistoryCalendar from './history/HistoryCalendar';
import StatDetailModal from './history/StatDetailModal';
import '../employeeAttendance.css';
import '../employeeAttendance-history-dashboard.css';

const STATUS_FILTERS = [
  { value: '', label: 'All' },
  { value: 'early', label: 'Early' },
  { value: 'normal', label: 'On time' },
  { value: 'late', label: 'Late' },
  { value: 'absent', label: 'Absent' },
];

/** Load enough history for calendar month navigation without manual date filters. */
const HISTORY_FETCH_RANGE = defaultDateRange(365);

const STAT_CARD_DEFS = [
  { key: 'totalDays', labelPrefix: 'Total Attendance', icon: MdCalendarMonth, accent: 'blue' },
  { key: 'absentDays', labelPrefix: 'Absent Days', icon: MdEventBusy, accent: 'slate' },
  { key: 'lateDays', labelPrefix: 'Late Days', icon: MdWarning, accent: 'red' },
  { key: 'earlyDays', labelPrefix: 'Total Early Days', icon: MdWbSunny, accent: 'amber' },
  { key: 'onTimeDays', labelPrefix: 'Total On-Time Days', icon: MdCheck, accent: 'indigo' },
];

/** When a status filter is active, show only its matching summary card. */
const STATUS_FILTER_STAT_KEY = {
  early: 'earlyDays',
  normal: 'onTimeDays',
  late: 'lateDays',
  absent: 'absentDays',
};

async function fetchAllHistoryRecords(token, range) {
  const limit = 200;
  let offset = 0;
  let all = [];
  let total = 0;

  do {
    const result = await employeeAttendanceAPI.getHistory({ ...range, limit, offset }, token);
    if (!result?.success) {
      throw new Error(result?.message || 'Failed to load history');
    }
    all = all.concat(result.records || []);
    total = result.total ?? all.length;
    offset += limit;
  } while (all.length < total && offset < 5000);

  return all;
}

function formatStatValue(key, stats) {
  return String(stats[key] ?? 0);
}

function EmployeeHistory() {
  const { getToken } = useEmployeeAttendanceAuth();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [summaryMonth, setSummaryMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  const [statModalKey, setStatModalKey] = useState(null);

  const loadHistory = useCallback(async () => {
    const token = getToken();
    if (!token) return;

    try {
      setLoading(true);
      setError(null);
      const data = await fetchAllHistoryRecords(token, HISTORY_FETCH_RANGE);
      setRecords(data);
    } catch (err) {
      setError(err.message || 'Failed to load history');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const filteredRecords = useMemo(
    () => filterRecordsByStatus(records, statusFilter),
    [records, statusFilter]
  );

  const monthName = useMemo(
    () => getMonthLabel(summaryMonth.year, summaryMonth.month),
    [summaryMonth]
  );

  const activeStatKey = STATUS_FILTER_STAT_KEY[statusFilter] ?? null;

  const statCards = useMemo(
    () =>
      STAT_CARD_DEFS.map((def) => ({
        ...def,
        label: `${def.labelPrefix} - ${monthName}`,
        hidden: activeStatKey ? activeStatKey !== def.key : false,
      })),
    [monthName, activeStatKey]
  );

  const monthRecords = useMemo(
    () => filterRecordsByMonth(records, summaryMonth.year, summaryMonth.month),
    [records, summaryMonth]
  );

  const stats = useMemo(() => computeSummaryStats(monthRecords), [monthRecords]);
  const recordsMap = useMemo(() => recordsByDateMap(filteredRecords), [filteredRecords]);

  const statModalRecords = useMemo(
    () => (statModalKey ? getRecordsForStatKey(monthRecords, statModalKey) : []),
    [statModalKey, monthRecords]
  );

  const statModalTitle = useMemo(() => {
    if (!statModalKey) return '';
    return statCards.find((c) => c.key === statModalKey)?.label ?? '';
  }, [statModalKey, statCards]);

  const handleViewMonthChange = useCallback((year, month) => {
    setSummaryMonth({ year, month });
    setStatModalKey(null);
  }, []);

  return (
    <div className="ea-dash">
      <header className="ea-dash-header">
        <div>
          <h1 className="ea-page-title">History</h1>
          <p className="ea-page-sub">Attendance insights and trends</p>
        </div>
      </header>

      <div className="ea-dash-filters-sticky">
        <div className="ea-dash-filters ea-dash-filters--status-only">
          <div className="ea-dash-filters__group" role="group" aria-label="Status filter">
            <span className="ea-dash-filters__label">Status</span>
            <div className="ea-dash-toggle ea-dash-toggle--wrap">
              {STATUS_FILTERS.map(({ value, label }) => (
                <button
                  key={value || 'all'}
                  type="button"
                  className={`ea-dash-toggle__btn ${statusFilter === value ? 'active' : ''}`}
                  onClick={() => {
                    setStatusFilter(value);
                    setSelectedDate(null);
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="ea-toast error show ea-dash-error" role="alert">
          {error}
        </div>
      )}

      {loading ? (
        <div className="ea-dash-loading">
          <div className="ea-dash-loading__spinner" />
          <p>Loading your attendance analytics…</p>
        </div>
      ) : (
        <>
          <section className="ea-dash-stats" aria-label="Summary statistics">
            {statCards.map(({ key, label, icon: Icon, accent, hidden }) => (
              <button
                key={key}
                type="button"
                className={`ea-dash-stat ea-dash-stat--${accent}${hidden ? ' ea-dash-stat--filtered-out' : ''}`}
                aria-hidden={hidden || undefined}
                tabIndex={hidden ? -1 : 0}
                disabled={hidden}
                onClick={() => setStatModalKey(key)}
                aria-label={`${label}, ${formatStatValue(key, stats)}. View details`}
              >
                <div className="ea-dash-stat__icon">
                  <Icon aria-hidden />
                </div>
                <div className="ea-dash-stat__body">
                  <p className="ea-dash-stat__label">{label}</p>
                  <p className="ea-dash-stat__value">{formatStatValue(key, stats)}</p>
                </div>
              </button>
            ))}
          </section>

          {statModalKey && (
            <StatDetailModal
              title={statModalTitle}
              records={statModalRecords}
              statKey={statModalKey}
              onClose={() => setStatModalKey(null)}
            />
          )}

          <HistoryCalendar
            recordsMap={recordsMap}
            selectedDate={selectedDate}
            onSelectDate={setSelectedDate}
            onViewMonthChange={handleViewMonthChange}
          />
        </>
      )}
    </div>
  );
}

export default EmployeeHistory;